import React from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { Clock } from 'lucide-react';
import { SocialIcon } from '../icons/SocialIcon';
import type { Post } from '../../types/calendar';
import type { SocialPlatform } from '../icons/SocialIcon';
import { ErrorHandlingMedia } from '../common/ErrorHandlingMedia';
import { normalizeMedia } from '../../utils/mediaUtils';

interface PostPreviewProps {
  post: Post;
}

export const PostPreview: React.FC<PostPreviewProps> = ({ post }) => {
  const getStatusBadge = (status: Post['status']) => {
    switch (status) {
      case 'draft':
        return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">📝 Draft</span>;
      case 'scheduled':
        return <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">📅 Scheduled</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">⏳ Pending</span>;
      case 'ready_to_publish':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">🚀 Ready</span>;
      case 'published':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">✅ Published</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">❌ Rejected</span>;
      default:
        return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">{status}</span>;
    }
  };

  const getFormattedTime = () => {
    // Try scheduledAt first, then fallback to createdAt
    const dateString = post.scheduledAt || post.createdAt;
    
    if (dateString) {
      try {
        const date = parseISO(dateString);
        if (isValid(date)) {
          return format(date, 'HH:mm');
        }
      } catch {
        // Fallback to new Date if parseISO fails
        try {
          const date = new Date(dateString);
          if (isValid(date)) {
            return format(date, 'HH:mm');
          }
        } catch {
          return '';
        }
      }
    }
    return '';
  };

  const getMediaFormatEmoji = (post: Post) => {
    // Determine media format based on post properties
    const normalizedMedia = normalizeMedia(post.media);
    
    if (!normalizedMedia) {
      return '📝'; // Text only
    }
    
    if (normalizedMedia.type === 'video') {
      return '🎬'; // Video
    }
    
    if (normalizedMedia.type === 'image') {
      return '🖼️'; // Image
    }
    
    return '📱'; // Default fallback
  };

  const getPostFormatEmoji = (post: Post) => {
    // Check if it's a Reel/Short based on content or platform-specific indicators
    const content = post.content.toLowerCase();
    const isReel = content.includes('#reel') || content.includes('#short') || 
                   post.platforms.some(platform => 
                     ['instagram', 'tiktok', 'youtube'].includes(platform.toLowerCase())
                   );
    
    if (isReel) {
      return '📱'; // Reel/Short
    }
    
    return '📸'; // Regular post
  };

  return (
    <div className="w-full sm:w-[640px] md:w-[700px] lg:w-[800px] max-w-full sm:max-w-4xl bg-white border border-black rounded-lg shadow-lg p-4 z-50 overflow-hidden">
      {/* Header with brand, time and status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {post.brandLogo && (
            <img 
              src={post.brandLogo} 
              alt={post.brandName || 'Brand logo'}
              className="w-9 h-9 rounded-full object-cover flex-shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div className="flex flex-col">
            {post.brandName && (
              <span className="text-sm font-medium text-gray-800">{post.brandName}</span>
            )}
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <Clock className="w-3 h-3" />
              <span>{getFormattedTime() || 'No time'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Platforms */}
          <div className="flex gap-2">
            {post.platforms.map((platform) => (
              <SocialIcon 
                key={platform}
                platform={platform as SocialPlatform}
                size={20}
              />
            ))}
          </div>
          {getStatusBadge(post.status)}
        </div>
      </div>

      {/* Main content area - horizontal layout */}
      <div className="flex flex-col md:flex-row gap-4 mb-3">
        {/* Media section */}
        <div className="flex-shrink-0">
          {(() => {
            const normalizedMedia = normalizeMedia(post.media);
            return normalizedMedia && (
              <div className="w-full md:w-64 h-64 rounded-lg overflow-hidden bg-gray-100 mb-3">
                <ErrorHandlingMedia
                  src={normalizedMedia.url}
                  alt="Post media"
                  type={normalizedMedia.type}
                  className="w-full h-full object-cover"
                  controls={normalizedMedia.type === 'video'}
                  autoPlayOnMount={normalizedMedia.type === 'video'}
                  muted={true}
                />
              </div>
            );
          })()}
          
          {/* Media Format and Post Format tags - below image */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              <div className="bg-primary-50 text-primary-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <span>{getMediaFormatEmoji(post)}</span>
                <span>{(() => {
                  const normalizedMedia = normalizeMedia(post.media);
                  return normalizedMedia ? (normalizedMedia.type === 'video' ? 'Video' : 'Image') : 'Text';
                })()}</span>
              </div>
              <div className="bg-primary-50 text-primary-700 text-xs px-2 py-1 rounded-full flex items-center gap-1">
                <span>{getPostFormatEmoji(post)}</span>
                <span>{getPostFormatEmoji(post) === '📱' ? 'Reel' : 'Regular'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Content section */}
        <div className="flex-1 flex flex-col">
          {/* Content */}
          <div className="flex-1">
            <p className="text-sm text-gray-800 leading-relaxed break-words overflow-wrap-anywhere whitespace-pre-wrap">
              {post.content}
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}; 